import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../../config';
import { formatDatePSTShort } from '../../utils/dateUtils';
import PaymentHistory from './PaymentHistory';
import './BillingDashboard.css';

interface InvoiceItem {
  id: string;
  line_number: number;
  description: string;
  item_type: string;
  quantity: number;
  unit_price: number;
  line_total: number;
}

interface Payment {
  id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  receipt_number?: string;
  notes?: string;
}

interface Invoice {
  id: string;
  invoice_number: string;
  invoice_date: string;
  due_date: string;
  status: string;
  subtotal: number;
  tax: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  student_id: string;
  student_name?: string;
  parent_id: string;
  parent_name?: string;
  payment_plan_id?: string;
  items: InvoiceItem[];
  payments?: Payment[];
  notes?: string;
}

interface InvoiceDetailProps {
  invoiceId: string;
  onClose: () => void;
  onPay?: (invoiceId: string) => void;
  onDownload?: (invoiceId: string) => void;
  onSendEmail?: (invoiceId: string) => void;
  onRecordPayment?: (invoiceId: string) => void;
  onCancel?: (invoiceId: string) => void;
  isAdmin?: boolean;
}

const InvoiceDetail: React.FC<InvoiceDetailProps> = ({
  invoiceId,
  onClose,
  onPay,
  onDownload,
  onSendEmail,
  onRecordPayment,
  onCancel,
  isAdmin = false
}) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'details' | 'payments'>('details');

  useEffect(() => {
    fetchInvoice();
  }, [invoiceId]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { getValidToken } = await import('../../utils/tokenRefresh');
      const token = await getValidToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.get(
        `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}`,
        { headers }
      );
      setInvoice(response.data);
    } catch (err: any) {
      console.error('Error fetching invoice:', err);
      setError(err.response?.data?.detail || 'Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (onDownload) {
      onDownload(invoiceId);
    } else {
      // Default PDF download - use pdf-url endpoint (similar to consent forms)
      try {
        const { getValidToken } = await import('../../utils/tokenRefresh');
        const token = await getValidToken();
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(
          `${config.BACKEND_URL}/api/billing/invoices/${invoiceId}/pdf-url`,
          { headers }
        );
        
        if (response.data.success && response.data.pdf_url) {
          window.open(response.data.pdf_url, '_blank', 'noopener,noreferrer');
        } else {
          alert('Failed to load PDF. Please try again.');
        }
      } catch (err: any) {
        console.error('Error fetching PDF URL:', err);
        alert(err.response?.data?.detail || 'Failed to load PDF. Please try again.');
      }
    }
  };

  const handleSendEmail = () => {
    if (onSendEmail) {
      onSendEmail(invoiceId);
    }
  };

  const handleRecordPayment = () => {
    if (onRecordPayment) {
      onRecordPayment(invoiceId);
    }
  };

  const handleCancel = () => {
    if (onCancel && window.confirm('Are you sure you want to cancel this invoice?')) {
      onCancel(invoiceId);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      'paid': 'status-badge status-paid',
      'partially_paid': 'status-badge status-partially-paid',
      'sent': 'status-badge status-sent',
      'overdue': 'status-badge status-overdue',
      'draft': 'status-badge status-draft',
      'cancelled': 'status-badge status-cancelled'
    };
    
    const statusLabels: { [key: string]: string } = {
      'paid': 'Paid',
      'partially_paid': 'Partially Paid',
      'sent': 'Sent',
      'overdue': 'Overdue',
      'draft': 'Draft',
      'cancelled': 'Cancelled'
    };
    
    return (
      <span className={statusClasses[status] || 'status-badge'}>
        {statusLabels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="loading">Loading invoice...</div>
        </div>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="error-message">Error: {error || 'Invoice not found'}</div>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  const isOverdue = invoice.status === 'overdue' || 
    (invoice.status === 'sent' && new Date(invoice.due_date) < new Date());

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content invoice-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Invoice {invoice.invoice_number}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="invoice-detail-header">
            <div className="invoice-detail-info">
              <div className="info-row">
                <span className="info-label">Status:</span>
                {getStatusBadge(invoice.status)}
              </div>
              <div className="info-row">
                <span className="info-label">Invoice Date:</span>
                <span className="info-value">{formatDatePSTShort(invoice.invoice_date)}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Due Date:</span>
                <span className={`info-value ${isOverdue ? 'overdue-date' : ''}`}>
                  {formatDatePSTShort(invoice.due_date)}
                </span>
              </div>
              {invoice.student_name && (
                <div className="info-row">
                  <span className="info-label">Student:</span>
                  <span className="info-value">{invoice.student_name}</span>
                </div>
              )}
              {invoice.parent_name && (
                <div className="info-row">
                  <span className="info-label">Parent:</span>
                  <span className="info-value">{invoice.parent_name}</span>
                </div>
              )}
            </div>
            
            <div className="invoice-detail-summary">
              <div className="summary-row">
                <span className="summary-label">Subtotal:</span>
                <span className="summary-value">${invoice.subtotal.toFixed(2)}</span>
              </div>
              {invoice.tax > 0 && (
                <div className="summary-row">
                  <span className="summary-label">Tax:</span>
                  <span className="summary-value">${invoice.tax.toFixed(2)}</span>
                </div>
              )}
              <div className="summary-row">
                <span className="summary-label">Total:</span>
                <span className="summary-value total-amount">${invoice.total.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Amount Paid:</span>
                <span className="summary-value">${invoice.amount_paid.toFixed(2)}</span>
              </div>
              <div className="summary-row">
                <span className="summary-label">Balance Due:</span>
                <span className={`summary-value ${invoice.balance_due > 0 ? 'balance-due' : ''}`}>
                  ${invoice.balance_due.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          
          <div className="invoice-detail-tabs">
            <button
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Invoice Items
            </button>
            <button
              className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
            >
              Payment History
            </button>
          </div>
          
          {activeTab === 'details' && (
            <div className="invoice-detail-content">
              <table className="invoice-items-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Description</th>
                    <th>Quantity</th>
                    <th>Unit Price</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.items.map((item) => (
                    <tr key={item.id}>
                      <td>{item.line_number}</td>
                      <td>{item.description}</td>
                      <td>{item.quantity}</td>
                      <td>${item.unit_price.toFixed(2)}</td>
                      <td>${item.line_total.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {invoice.notes && (
                <div className="invoice-notes">
                  <h4>Notes</h4>
                  <p>{invoice.notes}</p>
                </div>
              )}
            </div>
          )}
          
          {activeTab === 'payments' && (
            <div className="invoice-detail-content">
              <PaymentHistory invoiceId={invoiceId} />
            </div>
          )}
        </div>
        
        <div className="modal-footer">
          <div className="modal-actions">
            <button className="btn btn-secondary" onClick={handleDownload}>
              Download PDF
            </button>
            {isAdmin && onSendEmail && (
              <button className="btn btn-secondary" onClick={handleSendEmail}>
                Send Email
              </button>
            )}
            {isAdmin && onRecordPayment && invoice.balance_due > 0 && (
              <button className="btn btn-secondary" onClick={handleRecordPayment}>
                Record Payment
              </button>
            )}
            {invoice.balance_due > 0 && onPay && (
              <button className="btn btn-primary" onClick={() => onPay(invoiceId)}>
                Pay ${invoice.balance_due.toFixed(2)}
              </button>
            )}
            {isAdmin && onCancel && invoice.status !== 'cancelled' && invoice.status !== 'paid' && (
              <button className="btn btn-danger" onClick={handleCancel}>
                Cancel Invoice
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceDetail;

